'use client'

// DetectionScanner — the "machine vision" behind the FP recognition boxes.
// No model, no inference: the client already knows every object's identity
// and footprint, so recognition is just projecting known AABBs into screen
// space. Runs a throttled ~10Hz pass inside useFrame, writes results to the
// detection mailbox (lib/detectionState), renders nothing itself.
//
// Feel rules (enhance, don't distract): FP-only, close range with exit
// hysteresis so boxes don't flicker at the boundary, at most 4 at once
// (Victor and offerings outrank scenery), and anything too small, too
// huge (you're basically inside it), or off-screen is dropped.

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import useGameStore from '../../../stores/gameStore'
import {
  POINTS_OF_INTEREST, restPoints, TREES, STREET_LIGHTS, BINS, BIN_BAGS,
  DUMPSTER,
} from '../../../lib/world/sceneConfig'
import { useObstacles } from '../obstacles/ObstacleProvider'
import { detection } from '../../../lib/detectionState'
import { FEATURES } from '../../../lib/features'

const SCAN_INTERVAL = 0.1   // seconds between passes (~10Hz)
const RANGE = 20            // metres: scenery enters the visor here
const RANGE_VICTOR = 26     // he reads from further out
const EXIT_FACTOR = 1.15    // hysteresis: leave at range * this
const MAX_BOXES = 6
const RANGE_MINOR = 13   // small scatter (trees, bins, lights) enters closer
// Colliders smaller than this in both axes are poles/posts, not buildings.
const BUILDING_MIN_FOOTPRINT = 6

// POI name → [label word, halfX, halfZ, height]. Footprints are hand-tuned
// approximations of the models at those POIs; unlisted POIs don't box.
const POI_LABELS = {
  drinkAlcohol: ['bar', 4, 4, 4.5],
  drinkCoffee: ['coffee', 3.5, 3.5, 4.5],
  eatChinese: ['takeaway', 4, 4, 4.5],
  eatChinese_2: ['takeaway', 4, 4, 4.5],
  eatBakery: ['bakery', 4, 4, 4.5],
  eatHealth: ['apple tree', 2.5, 2.5, 5],
  eatPizza: ['pizza place', 4, 4, 4.5],
  eatSoup: ['soup cart', 2.8, 2.8, 3.2],
  // the street south of the pizza place (7 Oct); the spot is in front of
  // the door, so the box reaches back over the shopfront
  eatJapanese: ['japanese', 4, 4, 4.5],
  eatBakeryEarly: ['bakery', 4, 4, 4.5],
  eatTaco: ['taco truck', 3, 3, 3.2],
  shopMags: ['newsagent', 4, 4, 4],
  shopGallery: ['gallery', 4, 4, 5],
  shopWorkshop: ['workshop', 4.5, 4.5, 6],
  shopTech: ['tech shop', 4.5, 4.5, 5],
  jobCentre: ['job centre', 4.5, 5, 6],
  wishShelter: ['shelter', 3.4, 3.4, 4],
  wishBench: ['bench', 2.6, 2.6, 2.2],
  wishKeys: ['the keys', 3, 3, 3.4],
  wishMarket: ['night market', 10, 10, 3.8],
  eatJunk: ['burgers', 4.5, 4.5, 5],
  gamble: ['casino', 5, 5, 6],
  laundry: ['laundrette', 4, 4, 4.5],
  museum: ['museum', 5, 5, 6],
  phone: ['payphone', 0.7, 0.7, 2.4],
  rave: ['speakers', 2, 2, 2.6],
  rest: ['nest', 4.5, 4.5, 3],
  salon: ['salon', 4, 4, 4.5],
  shrine: ['shrine', 2.2, 2.2, 3],
  surfInternet: ['internet cafe', 4, 4, 5],
  tattoo: ['tattoo parlour', 4, 4, 5],
  watchMovie: ['cinema', 5, 5, 6],
  garden: ['greenhouse', 3, 3, 3.2],
}

// Landmarks that aren't POIs.
const EXTRAS = [
  { id: 'det_tower', word: 'radio tower', x: -78, z: -70, hx: 3.5, hz: 3.5, h: 18 },
  { id: 'det_lake', word: 'lake', x: -73.65, z: -0.68, hx: 20, hz: 22.5, h: 0.6 },
]

const _v = new THREE.Vector3()

export function DetectionScanner() {
  const { obstacles } = useObstacles()
  const acc = useRef(0)
  const prevIds = useRef(new Set())

  const staticCatalog = useMemo(() => {
    const list = []
    for (const p of POINTS_OF_INTEREST) {
      const l = POI_LABELS[p.name]
      if (!l) continue
      list.push({ id: `det_${p.id}`, word: l[0], x: p.position[0], z: p.position[2], hx: l[1], hz: l[2], h: l[3], prio: 2 })
    }
    for (const p of restPoints()) {
      list.push({ id: `det_${p.name}`, word: 'nest', x: p.position[0], z: p.position[2], hx: 6, hz: 6, h: 3, prio: 2 })
    }
    for (const e of EXTRAS) list.push({ ...e, prio: 2 })
    // Small scatter: trees, street lights, bins, bags, dumpsters. Closer
    // entry range + lowest priority so they fill spare box slots without
    // shouting over places, Victor, or offerings.
    for (const t of TREES) list.push({ id: `det_${t.id}`, word: 'tree', x: t.position[0], z: t.position[2], hx: 1.5, hz: 1.5, h: 5.5, prio: 3, range: RANGE_MINOR })
    for (const l of STREET_LIGHTS) list.push({ id: `det_${l.id}`, word: 'street light', x: l.position[0], z: l.position[2], hx: 0.5, hz: 0.5, h: 4.5, prio: 3, range: RANGE_MINOR })
    for (const b of BINS) list.push({ id: `det_${b.id}`, word: 'bin', x: b.position[0], z: b.position[2], hx: 0.55, hz: 0.55, h: 1.1, prio: 3, range: RANGE_MINOR })
    for (const b of BIN_BAGS) list.push({ id: `det_${b.id}`, word: 'bin bags', x: b.position[0], z: b.position[2], hx: 0.7, hz: 0.7, h: 0.8, prio: 3, range: RANGE_MINOR })
    for (const d of DUMPSTER) list.push({ id: `det_${d.id}`, word: 'dumpster', x: d.position[0], z: d.position[2], hx: 1.6, hz: 1.4, h: 1.6, prio: 3, range: RANGE_MINOR })
    // Generic 'building' boxes come from the REAL colliders in obstacles.glb,
    // the same source collision uses, so a box only ever appears where there
    // is actually geometry. (This used to read the placeholder-town data in
    // sceneConfig, whose buildings were deleted long ago: that is where the
    // rogue boxes over empty ground came from.) Small colliders are poles and
    // posts, not buildings, so they're filtered out by footprint.
    const labelled = list.filter((e) => e.prio === 2)
    ;(obstacles || []).forEach((o, i) => {
      if (!o.box) return
      const w = o.box.max.x - o.box.min.x
      const d = o.box.max.z - o.box.min.z
      if (Math.max(w, d) < BUILDING_MIN_FOOTPRINT) return
      const cx = (o.box.min.x + o.box.max.x) / 2
      const cz = (o.box.min.z + o.box.max.z) / 2
      if (labelled.some((e) => Math.hypot(e.x - cx, e.z - cz) < 8)) return
      list.push({
        id: `det_bld_${i}`, word: 'building',
        x: cx, z: cz, hx: w / 2, hz: d / 2,
        h: Math.max(2, o.box.max.y), prio: 2,
      })
    })
    return list
  }, [obstacles])

  useFrame((state, dt) => {
    acc.current += dt
    if (acc.current < SCAN_INTERVAL) return
    acc.current = 0

    const g = useGameStore.getState()
    const active = FEATURES.detection && !g.droneActive && g.uiVisible
    if (!active) {
      if (detection.boxes.length) {
        detection.boxes = []
        detection.version++
        prevIds.current.clear()
      }
      return
    }

    const cam = state.camera
    const width = state.size.width
    const height = state.size.height
    const px = cam.position.x
    const pz = cam.position.z

    // Test one candidate and push a box to `out` if it reads on screen. Kept
    // as a local so the static catalog (~150 items) is iterated in place each
    // pass instead of spread into a fresh array (steady GC while walking).
    const out = []
    const testOne = (c) => {
      const dist = Math.hypot(c.x - px, c.z - pz)
      const r = c.range || RANGE
      if (dist > (prevIds.current.has(c.id) ? r * EXIT_FACTOR : r)) return

      // Project the 8 AABB corners; every corner must be in front of the
      // camera (a behind-camera corner mirrors and the rect goes wild).
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      for (let i = 0; i < 8; i++) {
        _v.set(
          c.x + ((i & 1) ? c.hx : -c.hx),
          (i & 2) ? c.h : 0.05,
          c.z + ((i & 4) ? c.hz : -c.hz),
        )
        _v.project(cam)
        if (_v.z >= 1) return
        const sx = (_v.x * 0.5 + 0.5) * width
        const sy = (-_v.y * 0.5 + 0.5) * height
        if (sx < minX) minX = sx
        if (sx > maxX) maxX = sx
        if (sy < minY) minY = sy
        if (sy > maxY) maxY = sy
      }

      const w = maxX - minX
      const h = maxY - minY
      if (w < 18 || h < 14) return                                  // unreadable speck
      if (w > width * 0.85 || h > height * 0.85) return             // you're inside it
      if (maxX < 10 || minX > width - 10 || maxY < 10 || minY > height - 10) return

      out.push({ id: c.id, word: c.word, x: minX, y: minY, w, h, dist, prio: c.prio, gold: c.prio < 2 })
    }

    // Dynamic candidates (Victor + offerings), then the static catalog in place.
    const npc = g.npcStates?.[0]
    if (npc?.pos) {
      testOne({ id: 'det_victor', word: 'victor', x: npc.pos.x, z: npc.pos.z, hx: 0.55, hz: 0.55, h: 1.15, prio: 0, range: RANGE_VICTOR })
    }
    for (const e of g.entityStates || []) {
      if (e.type === 'SACRIFICE') {
        testOne({ id: `det_${e.id}`, word: 'offering', x: e.pos.x, z: e.pos.z, hx: 0.9, hz: 0.9, h: 1.7, prio: 1 })
      }
    }
    for (const c of staticCatalog) testOne(c)

    out.sort((a, b) => (a.prio - b.prio) || (a.dist - b.dist))
    const kept = out.slice(0, MAX_BOXES)
    prevIds.current.clear()
    for (const k of kept) prevIds.current.add(k.id)
    detection.boxes = kept
    detection.version++
  })

  return null
}

export default DetectionScanner
